/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useMsal} from "@azure/msal-react";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PageviewOutlined from "@mui/icons-material/PageviewOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/Refresh";
import {CircularProgress, IconButton, Tooltip} from '@mui/material';
import {MRT_ColumnDef} from "material-react-table";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {toast} from "react-hot-toast";
import {usePostRequest} from "../../../../../api/helper";
import {config} from "../../../../../AppConfig/config";
import {loginRequest, requestIdToken} from "../../../../../auth/authConfig";
import {tableButtonGray} from "../../../../../constants/colors";
import {ESensorDbSensorTypes} from "../../../../../constants/globalVariables";
import {stringToDateToLocaleString} from "../../../../../helpers/helpers";
import {useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import {setGlobalDialogComponent, setIsGlobalDialogOpen} from "../../../../../redux/ducks/GlobalDialog/slice";
import {setGlobalRightClickMenuData, setGlobalRightClickMenuPosition} from "../../../../../redux/ducks/GlobalRightClickMenu/slice";
import {ELoadingStateTypes, selectLoadingState, setLoadingState1} from "../../../../../redux/ducks/Misc/slice";
import {useSelectRefreshTableCount} from "../../../../../redux/ducks/Wizard/SliceProvider";
import {useAppDispatch, useAppSelector} from "../../../../../redux/hooks";
import ArgosButton, {ArgosLoadingButton} from "../../../../components/Buttons/ArgosButton";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import ArgosDialog from "../../../../components/Dialogs/ArgosDialog";
import AddNewSensorPopUp from "../../../../components/Dialogs/SensorsDialogs/AddNewSensorPopUp";
import {ArgosAddIcon} from "../../../../components/Icons/ArgosActionIcons";
import {DefaultButtonSpinner} from "../../../../components/Loader/DefaultButtonSpinner";
import {ArgosTable} from "../../../../components/Table/ArgosTable";
import {addRadarEndpoint, getRadarLandingEndpoint, SENSOR_REQUEST_ENDPOINTS, SensorRadarRow} from "../constants";
import {Edit as EditIcon} from "@mui/icons-material";
import EditSensorDialog from "./EditSensorDialog";

const SENSOR_DISPLAY_NAME = 'Radar'
const SENSOR_TYPE_NAME = ESensorDbSensorTypes.RADAR

const Table = () => {
  const hasWriteAccess = useHasWriteAccess()
  const {instance, accounts} = useMsal();
  const [tableData, setTableData] = useState<SensorRadarRow[]>([]);
  const [isTableError, setIsTableError] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const refresh = useSelectRefreshTableCount()
  const [isOpen, setIsOpen] = useState(false);
  const [isDownloadingFile, setIsDownloadingFile] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  useEffect(() => {
    setIsTableLoading(true)
    fetchData()
  }, [refresh])

  const handleDownloadTemplateFile = async (row: any) => {
    let url = `${config.url.API_URL}/HttpTriggerDownloadFile?request=sensorRadarSheet`
    const idTok = await requestIdToken(instance, accounts)
    const supplier = row.original.supplier.replace("/", "_")
    const component = row.original.component.replace("/", "_")
    const version = row.original.version
    const id = row.original.id
    const fileName = `${supplier}_${component}_${version}_${id}.xlsx`
    setIsDownloadingFile(true)
    const req_body = {
      "id": id,
      "fileName": fileName,
      "supplier": supplier,
      "component": component,
      "version": version
    }
    fetch(url, {
      method: "POST",
      headers: {
        'Content-Type': "application/json",
        'Authorization': `Bearer ${idTok}`
      },
      body: JSON.stringify(req_body)
    }).then((res) => {
      if (res.status === 401)
        instance.loginRedirect(loginRequest).catch(e => {
            console.log(e)
          }
        )
      res.blob().then((blob) => {
        const fileURL = window.URL.createObjectURL(blob)
        let alink = document.createElement('a')
        alink.href = fileURL;
        alink.download = fileName
        alink.click();
        setIsDownloadingFile(false)
      })
    }).catch((e) => {
      console.log(e)
    })

  }

  const isDeleteRadarLoadingState = useAppSelector((state) => selectLoadingState(state, ELoadingStateTypes.DELETE_RADAR))
  const isDeleteRadarLoading = isDeleteRadarLoadingState?.isLoading ?? false
  const isDeleteRadarLoadingData = isDeleteRadarLoadingState?.data ?? null

  const rowActions = (row: any, miscProps: any) => {
    const _row = row.original
    const id = _row?.id
    const url = `/radarPerformance?id=${id}`

    const isCurrentRadarDeleteLoading = isDeleteRadarLoadingData?.id === id
    // const deleteDisabled = !isCurrentRadarDeleteLoading && isDeleteRadarLoading
    return (
      _row.sub ? "" :
        <ArgosFlexbox>
          <Tooltip title={"View Radar Performance"} arrow>
            <a
              onClick={(e) => {
                e.preventDefault()
                const component = {
                  'params': {id},
                  'name': 'SensorRadarItemDetails'
                }
                dispatch(setGlobalDialogComponent(component))
                dispatch(setIsGlobalDialogOpen(true))
              }}
              onContextMenu={(e) => {
                e.preventDefault()
                dispatch(setGlobalRightClickMenuData({url}))
                dispatch(setGlobalRightClickMenuPosition({x: e.pageX, y: e.pageY}))
              }}
              href={url}
              target={'_blank'}
            >
              <IconButton size={'large'}>
                <PageviewOutlined fontSize="medium" style={{color: tableButtonGray}}/>
              </IconButton>
            </a>
          </Tooltip>

          {hasWriteAccess &&
            <IconButton size="large" onClick={() => miscProps.onClickEditIcon(row)}>
              <EditIcon fontSize="medium" style={{color: tableButtonGray}}/>
            </IconButton>
          }

          {isDownloadingFile ? <CircularProgress/> : hasWriteAccess &&
            <Tooltip title="Download Sensor Radar Template" arrow>
              <IconButton onClick={() => handleDownloadTemplateFile(row)} size={'large'}>
                <FileDownloadIcon style={{color: tableButtonGray}}/>
              </IconButton>
            </Tooltip>}

          {
            hasWriteAccess && (<Tooltip title={`Delete ${SENSOR_DISPLAY_NAME}`} arrow>
              <IconButton
                disabled={isDeleteRadarLoading}
                onClick={(e) => {
                  // deleteProgram({id: adasProgramId, name: programName, modelYear})
                  const rowData = row.original
                  onClickDeleteSensor(rowData)
                }}
                size="large"
              >
                {
                  isDeleteRadarLoading ?
                    <DefaultButtonSpinner/> :
                    <DeleteIcon fontSize="medium" style={{color: tableButtonGray}}/>
                }
              </IconButton>
            </Tooltip>)
          }
        </ArgosFlexbox>
    )
  }

  const sensorRadarTableColumns = useMemo<MRT_ColumnDef<SensorRadarRow>[]>(
    () => [
      {
        accessorFn: (row: SensorRadarRow) => row?.sub ? row.supplier ?? '' : "",
        id: 'supplier',
        header: 'Supplier',
        size: 300,
      },
      {
        accessorFn: (row: SensorRadarRow) => row?.sub ? row.component ?? '' : "",
        id: 'component',
        header: 'Component',
        size: 300,
      },
      {
        accessorFn: (row: SensorRadarRow) => row?.sub ? "" : row?.version ?? '',
        id: 'version',
        header: 'Latest Version',
        size: 300,
      },
      {
        accessorFn: (row: SensorRadarRow) => row?.sub ? "" : row?.comments ?? '',
        id: 'comments',
        header: 'Comments',
        size: 300,
      },
      {
        accessorFn: (row: SensorRadarRow) => row?.sub ? "" : stringToDateToLocaleString(row?.dateCreated) ?? '',
        id: 'dateCreated',
        header: 'Last Updated',
        size: 300,
      },
    ],
    []
  )

  const togglePopUp = () => {
    setIsOpen(!isOpen);
  }

  const handleOpenAddRadarDialog = () => {
    setIsOpen(true);
  }

  const fetchHandle = usePostRequest(getRadarLandingEndpoint)
  const fetchData = useCallback(async () => {
    setIsTableLoading(true);
    fetchHandle()
      .then((data) => {
        setTableDataHelper(data, true)
      }).catch(err => {
      setTableDataHelper([], false)
    });
  }, [])

  const setTableDataHelper = (data: any, success: boolean) => {
    if (success) {
      setIsTableError(false)
      setTableData(data?.sensors_to_plot ?? [])
      setIsTableLoading(false)
    } else {
      setIsTableError(true)
      setIsTableLoading(false)
      setTableData([])
    }
  }

  const onClickDeleteSensor = (row: any) => {
    /**
     * When deleting a sensor, first check if it is allowed.
     */
    checkIsDeleteValid(row)
  }
  const checkIsDeleteValidHandle = usePostRequest(SENSOR_REQUEST_ENDPOINTS.shouldDeleteSensor)
  const [deleteDialogData, setDeleteDialogData] = useState<any>(null)
  const checkIsDeleteValid = (data: any) => {
    /**
     * If the sensor can be deleted, then show a popup to confirm the deletion.
     */
    // dispatch(setLoadingState({[ELoadingStateTypes.DELETE_RADAR]: {isLoading: true}}))
    dispatch(setLoadingState1(ELoadingStateTypes.DELETE_RADAR, true, {id: data.id}))
    let _deleteDialogData: any = {
      data: data
    }
    checkIsDeleteValidHandle({id: data.id}).then((res) => {
      if (res?.shouldDeleteSensor) {
        // Show message of what will be deleted and are you sure.
        _deleteDialogData.message = res?.message
      } else {
        // Show that the sensor cannot be deleted and why.
        _deleteDialogData.deleteInvalid = true
        _deleteDialogData.message = res?.message
      }
      setDeleteDialogData(_deleteDialogData)
    }).catch(err => {
      console.log(err)
      toast.error("Failed to delete radar")
      _deleteDialogData.deleteInvalid = true
      _deleteDialogData.message = "Failed to delete radar"
      setDeleteDialogData(_deleteDialogData)
    }).finally(() => {
      dispatch(setLoadingState1(ELoadingStateTypes.DELETE_RADAR, false, {id: data.id}))
    })
  }
  const deleteSensorHandle = usePostRequest(SENSOR_REQUEST_ENDPOINTS.deleteSensor)
  const deleteSensor = (data: any) => {
    dispatch(setLoadingState1(ELoadingStateTypes.DELETE_RADAR, true, {id: data.id}))
    deleteSensorHandle({id: data.id, sensorType: SENSOR_TYPE_NAME}).then((res) => {
      setDeleteDialogData(null)
      setTableDataHelper(res, true)
    }).catch(err => {
      console.log(err)
      toast.error("Failed to delete radar")
      const payload = {
        data,
        deleteInvalid: true,
        message: "Failed to delete radar"
      }
      setDeleteDialogData(payload)
    }).finally(() => {
      dispatch(setLoadingState1(ELoadingStateTypes.DELETE_RADAR, false, {id: data.id}))
    })
  }

  const commonEditSensorRootFieldsHandle = usePostRequest(SENSOR_REQUEST_ENDPOINTS.commonEditSensorRootFields)
  const commonEditSensorRootFields = (data: any) => {
    toast.loading('Editing radar...', {id: 'editRadar'})
    commonEditSensorRootFieldsHandle(data).then((res) => {
      setCommonEditSensorRootFieldsDialogData(null)
      setTableDataHelper(res, true)
    }).catch(err => {
      console.log(err)
      toast.error("Failed to edit radar")
    }).finally(() => {
      toast.dismiss('editRadar')
    })
  }
  const [commonEditSensorRootFieldsDialogData, setCommonEditSensorRootFieldsDialogData] = useState<any>(null)
  const onClickEditIcon = (row: any) => {
    const rowData = row.original
    setCommonEditSensorRootFieldsDialogData(rowData)
  }
  const miscProps = {
    onClickEditIcon
  }

  const topToolbarActions = (
    /* START */
    <div className="tool-icons tool-icons-table">
      <Tooltip title="Refresh" arrow style={{paddingTop: "7px"}}>
        <IconButton onClick={fetchData} size={'large'}>
          <RefreshOutlinedIcon style={{color: tableButtonGray}}/>
        </IconButton>
      </Tooltip>
      {
        hasWriteAccess && <Tooltip title={`Add New ${SENSOR_DISPLAY_NAME}`} arrow style={{paddingTop: "7px"}}>
          <IconButton onClick={handleOpenAddRadarDialog} size='large'>
            <ArgosAddIcon style={{color: tableButtonGray}}/>
          </IconButton>
        </Tooltip>
      }
    </div>
  );

  const deleteDialogMessage = deleteDialogData?.message
  const sensorToDeleteName = getFullSensorName(deleteDialogData?.data, ESensorDbSensorTypes.RADAR)

  const editSensorName = commonEditSensorRootFieldsDialogData ? getFullSensorName(commonEditSensorRootFieldsDialogData, ESensorDbSensorTypes.RADAR) : ''
  const editSensorDialogTitle = `Edit ${SENSOR_DISPLAY_NAME} ${editSensorName}`
  return (
    <div style={{overflow: 'auto', flex: 1}}>
      <ArgosTable
        uid={'radar_1'}
        columns={sensorRadarTableColumns}
        data={tableData}

        //expanding button
        enableExpanding
        muiExpandButtonProps={({row}) => ({
          // Show expand button only when row has sub-elements
          sx: {
            display: row.subRows?.length ? 'flex' : 'none'
          }
        })}
        //Actions row
        enableRowActions
        renderTopToolbarCustomActions={() => topToolbarActions}
        positionActionsColumn="last"
        displayColumnDefOptions={{
          'mrt-row-actions': {
            size: 250,
          },
        }}
        state={{isLoading: isTableLoading}}
        getSubRows={(originalRow) => originalRow?.sub}
        enablePagination={false}
        renderRowActions={({row}: any) => rowActions(row, miscProps)}
        muiTopToolbarProps={{
          sx: {justifyContent: "right !important"}
        }}
      />
      <ArgosDialog open={isOpen} maxWidth={'fit-content'} minWidth={'0px'} height={'fit-content'}
                   headerTitle={`Add New ${SENSOR_DISPLAY_NAME}`} headerTitleAlign={'left'} onClose={() => null}>
        <AddNewSensorPopUp handleClose={togglePopUp} setTableData={() => fetchData()} sensorType={SENSOR_TYPE_NAME}
                           currentTableData={tableData} addSensorEndpoint={addRadarEndpoint}/>
      </ArgosDialog>

      <ArgosDialog
        minWidth={'0px'} maxWidth={'30vw'} height={'fit-content'}
        open={deleteDialogData}
        onClose={() => {
          setDeleteDialogData(false)
        }}
        headerTitle={`Delete ${SENSOR_DISPLAY_NAME} ${sensorToDeleteName}`}
        headerTitleAlign={'left'}
      >
        <>
          {!deleteDialogData ? <></> :
            <ArgosFlexbox vertical>
              <div>{deleteDialogMessage}</div>
              <ArgosFlexbox alignItems={'center'} justifyContent={'center'}>
                <ArgosButton
                  disabled={isDeleteRadarLoading}
                  onClick={() => {
                    setDeleteDialogData(null)
                  }}
                >
                  CANCEL
                </ArgosButton>
                {
                  !deleteDialogData?.deleteInvalid &&
                  <ArgosLoadingButton
                    disabled={isDeleteRadarLoading}
                    onClick={() => {
                      deleteSensor(deleteDialogData?.data)
                    }}
                    isLoading={isDeleteRadarLoading}
                  >
                    DELETE
                  </ArgosLoadingButton>
                }
              </ArgosFlexbox>
            </ArgosFlexbox>
          }
        </>
      </ArgosDialog>

      <EditSensorDialog
        commonEditSensorRootFieldsDialogData={commonEditSensorRootFieldsDialogData}
        commonEditSensorRootFields={commonEditSensorRootFields}
        editSensorDialogTitle={editSensorDialogTitle}
        setCommonEditSensorRootFieldsDialogData={setCommonEditSensorRootFieldsDialogData}
      />
    </div>
  )
}

export default Table


export const getFullSensorName = (data: any, sensorType: ESensorDbSensorTypes) => {
  if (!data) return ''
  switch (sensorType) {
    case ESensorDbSensorTypes.ULTRASONIC:
    case ESensorDbSensorTypes.LIDAR:
    case ESensorDbSensorTypes.RADAR: {
      const supplier = data?.supplier ?? ''
      const component = data?.component ?? ''
      const version = data?.version ?? ''
      return `${supplier} ${component} ${version}`
    }
    case ESensorDbSensorTypes.CAMERA: {
      const sensorToDeleteSupplier = data?.cameraSupplier
      const sensorToDeleteComponent = data?.cameraModule
      const sensorToDeleteVersion = data?.version
      return `${sensorToDeleteSupplier} ${sensorToDeleteComponent} ${sensorToDeleteVersion}`
    }
  }
}